import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Link, Redirect } from 'react-router-dom';
import TitleHeader from '../../components/molecules/TitleHeader/TitleHeader';
import Loading from '../../components/atoms/Loading/Loading';
import ErrorBanner from '../../components/molecules/ErrorBanner/ErrorBanner';
import { PATHS } from '../../navigationConstants';
import {
  OPPORTUNITY_REFERENCING_STATUSES,
  ROLE_REFERENCING_STATUSES,
  SF_TENANCY_TYPES,
} from '../../appConstants';
import {
  getHouseholdSummary,
  getOcrReferencing,
  getPrimaryOffer,
  submitHomeletReference,
} from '../../api/salesforce';
import { referencingInfoCopy } from '../../content/main.json';
import { UserContext } from '../../providers/UserContext';
import { WalesContext } from '../../providers/WalesContext';
import Button from '../../components/atoms/Button/Button';
import HomeletReferencingForm from '../../components/molecules/HomeletReferencingForm/HomeletReferencingForm';
import './Referencing.scss';

const extractTenancyTermMonths = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const match = String(value).match(/\d+/);
  return match ? match[0] : '';
};

const mapSfRoleToHomeletTenantType = (role) => {
  switch ((role || '').trim()) {
    case '1 Lead Tenant':
      return '1';
    case '2 Tenant':
      return '2';
    case '3 Guarantor':
      return '3';
    case '4 Permitted Occupier':
      return '2';
    default:
      return '1';
  }
};

const Referencing = ({ history }) => {
  const { isWales } = useContext(WalesContext);
  const referencingInfoTerms = !isWales ? referencingInfoCopy : referencingInfoCopy.wales;
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState(null);
  const [provider, setProvider] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formTimeout, setFormTimeout] = useState(false);
  const [homeletSubmitError, setHomeletSubmitError] = useState('');
  const [initialPropertyAddress, setInitialPropertyAddress] = useState({});
  const [initialTenantData, setInitialTenantData] = useState({});
  const [initialRentalData, setInitialRentalData] = useState({});
  const [titleCopy, setTitleCopy] = useState(referencingInfoTerms.title);
  const {
    opportunityId,
    contactId,
    agreementType,
    opportunityStage,
    referencingStatus,
    referenceProvider,
    isReferencingExempt,
    tenancyType,
  } = useContext(UserContext);
  const hideTenancyTerm = /apt/i.test(agreementType || '');
  const normaliseSfId = (value) => (value || '').toString().substring(0, 15).toUpperCase();
  const shouldAllowMidTermSharerHomeletForm = (
    tenancyType === SF_TENANCY_TYPES.MID_TERM_CHANGE_OF_SHARER
    && !isReferencingExempt
    && (referenceProvider || '').toLowerCase() === 'homelet'
  );
  const shouldRedirectToReferencingProgress = (
    OPPORTUNITY_REFERENCING_STATUSES.includes(opportunityStage)
    && [
      ROLE_REFERENCING_STATUSES.IN_PROGRESS,
      ROLE_REFERENCING_STATUSES.COMPLETED,
    ].includes(referencingStatus)
    && !shouldAllowMidTermSharerHomeletForm
  );

  useEffect(() => {
    const fetchUrl = async () => {
      // eslint-disable-next-line camelcase
      const retry_appliant_id = true;
      // eslint-disable-next-line no-shadow
      const { referencingUrl, referenceProvider, error } = await getOcrReferencing(opportunityId, contactId, retry_appliant_id);
      if ((referenceProvider || '').toLowerCase() !== 'homelet') {
        if (!referencingUrl) {
          return history.push(PATHS.ERROR_REFERENCING_UNAVAILABLE);
        }
        setUrl(referencingUrl);
        setProvider('rightmove');
      } else {
        if (error === 'INVALID_APPLICANT_USER_ID') {
          return history.push(PATHS.ERROR_REFERENCING_INVALID_APPLICANT);
        }
        setUrl(null);
        setProvider('homelet');
        const primaryOffer = await getPrimaryOffer(opportunityId);
        const rawTenancyTerm = primaryOffer && primaryOffer.term;
        const parsedTenancyTermMonths = extractTenancyTermMonths(rawTenancyTerm);
        console.log('[HomeLet] Salesforce primary offer term (Term__c):', rawTenancyTerm);
        console.log('[HomeLet] Parsed tenancy term months:', parsedTenancyTermMonths);
        const unit = (primaryOffer && primaryOffer.unit) || {};
        const householdSummary = await getHouseholdSummary(opportunityId);
        const tenantRecord = (householdSummary || []).find(
          (item) => normaliseSfId(item.ContactId) === normaliseSfId(contactId),
        ) || {};
        const tenantContact = tenantRecord.Contact || {};

        setInitialPropertyAddress({
          houseNumber: unit.Street_No__c || '',
          houseName: unit.Name || '',
          addressLine1: unit.Street_Name__c || '',
          addressLine2: unit.County__c || '',
          town: unit.City__c || '',
          postcode: unit.Post_Code__c || '',
        });
        setInitialTenantData({
          firstName: tenantContact.FirstName || '',
          surname: tenantContact.LastName || '',
          emailAddress: tenantContact.Email || '',
          contactTelephoneNo: tenantContact.Phone || '',
          dateOfBirth: tenantContact.Birthdate || '',
          tenantTypeId: mapSfRoleToHomeletTenantType(tenantRecord.Role),
        });
        setInitialRentalData({
          tenancyTermMonths: parsedTenancyTermMonths,
          monthlyRental: primaryOffer && primaryOffer.monthlyRent !== undefined && primaryOffer.monthlyRent !== null
            ? primaryOffer.monthlyRent
            : '',
          shareOfRent: primaryOffer && primaryOffer.monthlyRent !== undefined && primaryOffer.monthlyRent !== null
            ? primaryOffer.monthlyRent
            : '',
          estimatedMoveInDate: primaryOffer && primaryOffer.opportunity && primaryOffer.opportunity.Move_In_Date__c
            ? primaryOffer.opportunity.Move_In_Date__c
            : '',
        });
      }

      return setLoading(false);
    };
    fetchUrl();
  }, []);

  useEffect(() => {
    if (provider !== 'rightmove' || !url) return undefined;

    const rightmoveListener = (e) => {
      if (url && url.includes(e.origin)) {
        if (e.data && e.data.callback) {
          if (e.data.callback === 'referenceApplicationDone') {
            const iframe = document.getElementById('referencing-iframe');
            if (iframe) {
              iframe.classList.add('referencing__iframe--submit');
            }
            setFormSubmitted(true);
            setTitleCopy(referencingInfoTerms.successTitle);
          }
          if (e.data.callback === 'referenceApplicationLoggedOut') {
            setFormTimeout(true);
          }
        }
      }
    };

    window.addEventListener('message', rightmoveListener);
    return () => window.removeEventListener('message', rightmoveListener);
  }, [provider, url, referencingInfoTerms.successTitle]);

  if (shouldRedirectToReferencingProgress) {
    return <Redirect to={PATHS.REFERENCING_PROGRESS} />;
  }

  return (
    <>

      <TitleHeader
        title={titleCopy}
        category={referencingInfoTerms.category}
        large
      />
      {loading && <Loading />}
      {formTimeout && (
        <ErrorBanner
          body={['Sorry this page has expired. Please refresh the page.']}
        />
      )}
      {!loading && !formTimeout
        && provider === 'rightmove'
        && (
          <div className="row referencing__iframe-container">
            <iframe
              className="col-12 referencing__iframe"
              title="referencing-iframe"
              src={url}
              id="referencing-iframe"
              name="iframeTenantPortal"
            />
          </div>
        )}
      {!loading && !formTimeout && provider === 'homelet' && (
        <HomeletReferencingForm
          submitButtonLabel={referencingInfoTerms.nextButton}
          submitError={homeletSubmitError}
          hideTenancyTerm={hideTenancyTerm}
          initialPropertyAddress={initialPropertyAddress}
          initialTenantData={initialTenantData}
          initialRentalData={initialRentalData}
          onSubmit={async (values) => {
            try {
              setHomeletSubmitError('');
              await submitHomeletReference(opportunityId, contactId, values);
              try {
                window.sessionStorage.setItem('homeletUploadDocsSession', 'pending');
              } catch (storageError) {
                console.warn('Unable to persist HomeLet upload-docs session flag.', storageError);
              }
              history.push(PATHS.UPLOAD_DOCS, { fromHomeletSubmit: true });
            } catch (error) {
              console.error('[Referencing] HomeLet API rejected the submission:', error);
              setHomeletSubmitError((error && error.message) || 'Unable to submit HomeLet reference right now. Please try again or contact support.');
            }
          }}
        />
      )}
      {formSubmitted
        && (

          <Link to={PATHS.REFERENCING_PROGRESS}>
            <Button title={referencingInfoTerms.nextButton} />
          </Link>
        )}
    </>
  );
};
Referencing.propTypes = {
  history: PropTypes.instanceOf(Object).isRequired,
};

export default Referencing;
